import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater, useImmer } from "use-immer";

import { IgButton, IgTooltip } from "@intelligems/ig-theme";
import { CustomEventDto, CustomEventFeType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { usePutCustomEvent } from "@src/api/custom-events/put-custom-event";
import {
  createCustomEventPayload,
  getTooltipErrorMessage,
  isSaveButtonDisabled,
} from "@src/features/customEvents/helpers";
import {
  resetNewCustomEvent,
  selectedCurrentCustomEvent,
} from "@src/store/slices/customEvents";
import { closeDrawer } from "@src/store/slices/drawers";
import { setCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { closeProductPicker } from "@src/store/slices/productPicker";

interface CustomEventDrawerFooterProps {
  subpageSelected: CustomEventFeType | null;
  setSubpageSelected: Updater<CustomEventFeType | null>;
  setIsCreating: Updater<boolean>;
  isCreating: boolean;
  renderLocation: "eventsManager" | "customMetric" | "keyMetric";
  setSelectedGeneric: Updater<string[]>;
  selectedCustomEventIds: string[];
  customEvents: CustomEventDto[];
  setDrawerPage: Updater<string>;
}

export const CustomEventDrawerFooter = ({
  subpageSelected,
  setSubpageSelected,
  setIsCreating,
  isCreating,
  renderLocation,
  setSelectedGeneric,
  selectedCustomEventIds,
  customEvents,
  setDrawerPage,
}: CustomEventDrawerFooterProps) => {
  const dispatch = useDispatch();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const experience = useSelector(selectedExperience);
  const [isArchived, _setIsArchived] = useImmer<boolean>(
    !!currentEvent?.archivedAtTs
  );

  const putCustomEvent = usePutCustomEvent({
    setSubpageSelected,
    setIsCreating,
    setSelectedGeneric,
    renderLocation,
    selectedCustomEventIds,
    dontAddAsSelected: experience ? Boolean(currentEvent?.id) : true,
    setDrawerPage,
  });
  const handleClose = () => {
    dispatch(resetNewCustomEvent());
    setIsCreating(false);
    setSubpageSelected(null);
    dispatch(closeDrawer("customEvents"));
    dispatch(closeProductPicker());
  };
  const handleSave = () => {
    if (subpageSelected) {
      if (currentEvent) {
        putCustomEvent.mutate({
          body: {
            customEvent: createCustomEventPayload(currentEvent),
          },
          pathParams: undefined,
          queryParams: undefined,
        });
      }
    } else {
      dispatch(
        setCustomMetrics({
          selectedCustomEventIds,
          allCustomEvents: customEvents,
        })
      );
      dispatch(closeDrawer("customEvents"));
    }
  };

  return (
    <Grid
      container
      justifyContent={"flex-end"}
      spacing={isArchived ? 0 : 2}
      sx={{
        marginTop: "auto",
        paddingBottom: "20px",
      }}
    >
      <Grid item marginTop={"10px"}>
        <IgButton color={"secondary"} onClick={() => handleClose()}>
          {isArchived ? "Close" : "Cancel"}
        </IgButton>
      </Grid>
      <Grid item marginTop={"10px"}>
        {!isArchived && (
          <IgTooltip
            title={
              !isCreating &&
              subpageSelected === null &&
              selectedCustomEventIds.length === 0
                ? "Select at least one custom metric to save."
                : !isCreating &&
                    subpageSelected === null &&
                    selectedCustomEventIds.length > 0
                  ? ""
                  : getTooltipErrorMessage(currentEvent)
            }
          >
            <IgButton
              sx={{ width: "100%" }}
              variant={"contained"}
              onClick={handleSave}
              loading={putCustomEvent.isPending}
              disabled={
                !isCreating &&
                subpageSelected === null &&
                selectedCustomEventIds.length === 0
                  ? true
                  : !isCreating &&
                      subpageSelected === null &&
                      selectedCustomEventIds.length > 0
                    ? false
                    : isSaveButtonDisabled(currentEvent)
              }
            >
              {!isCreating && subpageSelected === null
                ? `Save Custom Metric${selectedCustomEventIds.length > 1 ? "s" : ""}`
                : `${currentEvent?.id ? "Save" : "Create"} Event`}
            </IgButton>
          </IgTooltip>
        )}
      </Grid>
    </Grid>
  );
};
